import {
  Autocomplete,
  Grid,
  TextField as MuiTextField,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useMutation, useQueries } from "@tanstack/react-query";
import { App, Spin } from "antd";
import { Formik } from "formik";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import SelectField from "~/components/Extras/SelectField";
import TextField from "~/components/Extras/TextField";
import GenericFormContainer from "~/components/GenericFormContainer";
import MDBox from "~/components/MDBox";
import MDButton from "~/components/MDButton";
import { roles } from "~/config";
import useAuthorization from "~/hooks/useAuthorization";
import { debounce } from "~/pages/PlantSurvey/helper";
import { getGeoLocations } from "~/queries/locationMasterQuery";
import {
  useCreatePlottingMutation,
  useUpdatePlottingMutation,
} from "~/queries/plottingMasterQuery";

import { getLocations } from "~/services/location.service";
import { getPlantCategory } from "~/services/plant.service";
import { getPlotting } from "~/services/plotting.service";
import { autocompleteStyleOverrides } from "~/utils/utils";
import { updateInitialValues, updateValidationSchema } from "./helper";
import GoogleMap from "~/components/GoogleMaps";
import GoogleAutocomplete from "~/utils/components/GoogleAutocomplete";
import MyLocationMap from "~/components/MyLocationMap";

const CreatePlotting = () => {
  const theme = useTheme();
  const matchesXs = useMediaQuery(theme.breakpoints.down("md"));

  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [altitude, setAltitude] = useState(null);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [address, setAddress] = useState("");
  // React hooks
  const [submitButtonLabel, setSubmitButtonLabel] = useState("Submit");
  const [cardHeaderTitle, setCardHeaderTitle] = useState("Create New Plotting");
  const { userHasRequiredRole } = useAuthorization([roles.ADMIN]);

  const formRef = useRef(null);
  const childRef = useRef();
  // Router Hooks
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const { message } = App.useApp();

  // query mutation hooks
  const createPlottingMutation = useCreatePlottingMutation();
  const updatePlottingMutation = useUpdatePlottingMutation();
  // const locationData = useLocationQuery();

  const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_API_KEY;

  const locationData = useMutation({
    mutationFn: (payload) => getLocations(payload),
  });

  const geoLocation = getGeoLocations();

  const queriesData = useQueries({
    queries: [
      {
        queryKey: ["Land use", 0],
        queryFn: () => getPlantCategory("Land use"),
      },
      {
        queryKey: ["Aspect", 1],
        queryFn: () => getPlantCategory("Aspect"),
      },
      {
        queryKey: ["Soil_Texture", 2],
        queryFn: () => getPlantCategory("Soil Texture"),
      },
      {
        queryKey: ["plot_auto_id", location.state?.Plot_Auto_ID],
        queryFn: () => getPlotting(location.state?.Plot_Auto_ID),
      },
    ],
  });

  const handleSubmit = async (values, helpers) => {
    const datalocation = [latitude, longitude];
    try {
      const data = await geoLocation.mutateAsync(datalocation);
      if (data && data.results.length > 0) {
        console.log(data.results[0].elevation);
        setAltitude(data.results[0].elevation);
      } else {
        setAltitude(null);
      }
    } catch (error) {
      console.error("Error fetching elevation data:", error);
    }
    if (
      location.state &&
      location.pathname.includes("plotting-master/update")
    ) {
      console.log("During the update", params);
      try {
        const updateResponse = await updatePlottingMutation.mutateAsync({
          ...values,
          Latitude: latitude,
          Longitude: longitude,
          Altitude_meter: altitude,
          params: location.state?.Plot_Auto_ID,
          Location: address
        });
        message.success(updateResponse.data.message);
        navigate("/plotting-master");
      } catch (error) {
        message.error(error.response.data.message);

        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: error.response.data.message });
        helpers.setSubmitting(false);
      }
    } else {
      try {
        const createResponse = await createPlottingMutation.mutateAsync({
          ...values,
          Latitude: latitude,
          Longitude: longitude,
          Altitude_meter: altitude,
          Location: address
        });
        message.success(createResponse.data.message);
        navigate("/plotting-master");
      } catch (error) {
        message.error(error.response.data.message);

        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: error.response.data.message });
        helpers.setSubmitting(false);
      }
    }
  };

  /* new changes - tag v3.0.1 */

  const isPlottingUpdated = useMemo(
    () => queriesData[3].data?.data?.plantation ?? false,
    [queriesData]
  );

  // Function to fetch location name using latitude and longitude
  const fetchLocationName = useCallback(async (latitude, longitude) => {
    console.log("you")
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${GOOGLE_MAPS_API_KEY}`;
    const response = await fetch(url);
    const data = await response.json();
    if (data.status === "OK" && data.results.length > 0) {
      const locationName = data.results[0].formatted_address;
      return locationName;
    } else {
      throw new Error("No location found");
    }
  }, [GOOGLE_MAPS_API_KEY]);

  const fetchLocation = useCallback(
    async (latitude, longitude) => {
      try {
        const locationName = await fetchLocationName(latitude, longitude);

        return locationName;
      } catch (error) {
        console.error("Error fetching location name:", error);
      }
    },
    [fetchLocationName]
  );

  const handleLatitudeChange = useCallback(
    async (event) => {
      event.preventDefault();
      const newLatitude = event.target.value;
      setLatitude(newLatitude);
      const locationName = await fetchLocation(newLatitude, longitude);
      setAddress(locationName); // Update address state
    },
    [fetchLocation, longitude]
  );

  const handleLongitudeChange = useCallback(
    async (event) => {
      event.preventDefault();
      const newLongitude = event.target.value;
      setLongitude(newLongitude);
      const locationName = await fetchLocation(latitude, newLongitude);
      setAddress(locationName); // Update address state
    },
    [fetchLocation, latitude]
  );

  useEffect(() => {
    if (
      location.state &&
      location.pathname.includes("plotting-master/update")
    ) {
      console.log(location.state, "fetchData")
      setSubmitButtonLabel("Update");
      setCardHeaderTitle("Update Plotting");

      const { setFieldValue } = formRef.current;

      for (const key in location.state) {
        if (Object.hasOwnProperty.call(location.state, key)) {
          const value = location.state[key];
          setFieldValue(key, value);
        }
      }
      if (!location.state.Latitude && !location.state.Longitude && !location.state.Location) {
        // Fetch system latitude and longitude
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude: sysLatitude, longitude: sysLongitude } = position.coords;
            setLatitude(sysLatitude);
            setLongitude(sysLongitude);

            // Fetch location name based on system latitude and longitude
            try {
              const locationName = await fetchLocationName(sysLatitude, sysLongitude);
              setAddress(locationName);
            } catch (error) {
              console.error("Error fetching location name:", error);
            }
          },
          (error) => {
            console.error("Error getting system location:", error);
          }
        );
      } else {
        setLatitude(location.state.Latitude);
        setLongitude(location.state.Longitude);
        setAddress(location.state.Location);
      }
    } else {
      console.log("eheh")
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          console.log(position)
          const { latitude: sysLatitude, longitude: sysLongitude } = position.coords;
          console.log(sysLatitude)
          setLatitude(sysLatitude);
          setLongitude(sysLongitude);

          // Fetch location name based on system latitude and longitude
          try {
            const locationName = await fetchLocationName(sysLatitude, sysLongitude);
            setAddress(locationName);
          } catch (error) {
            console.error("Error fetching location name:", error);
          }
        },
        (error) => {
          console.error("Error getting system location:", error);
        }
      );
    }
  }, [location, fetchLocationName]);

  const handleSuggestionInput = (searchText) => {
    // if (searchText === '' || searchText.length < 4) return;
    setSearchQuery(searchText);
    locationData.mutate({ filter: searchText });
  };

  const debounceHandlerForSuggestions = useCallback(
    debounce(handleSuggestionInput, 500),
    []
  );

  function handleAutocompleteChange(value, fieldName, idFieldName) {
    if (!value?.label) {
      formRef.current.setFieldValue(fieldName.trim(), "");
      formRef.current.setFieldValue(idFieldName.trim(), "");
    } else {
      // do something else
      formRef.current.setFieldValue(fieldName.trim(), value?.label);
      formRef.current.setFieldValue(idFieldName.trim(), value?.value);
    }
  }

  function getGoogleAutocomplete(place) {
    if (place !== null) {
      const { structured_formatting, description, geometry } = place;
      const { lat, lng } = geometry;
      setLatitude(lat);
      setLongitude(lng);
      setAddress(description);
    }
  }

  useEffect(() => {
    locationData.mutate({ filter: searchQuery });
    // return () => {

    // }
  }, []);

  return (
    <GenericFormContainer
      cardHeaderTitle={cardHeaderTitle}
      navigateTo={location.state === true ? -1 : "/plotting-master"}
      p={2}
    >
      <Grid container spacing={2}>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <MDBox p={2}>
            <Formik
              initialValues={updateInitialValues}
              validationSchema={updateValidationSchema}
              onSubmit={handleSubmit}
              innerRef={formRef}
            >
              {({
                handleSubmit,
                values,
                setFieldValue,
                handleChange,
                handleBlur,
                touched,
                errors,
              }) => (
                <form onSubmit={handleSubmit}>
                  <Spin
                    tip={"Please wait..."}
                    spinning={
                      geoLocation.isLoading ||
                      createPlottingMutation.isLoading ||
                      updatePlottingMutation.isLoading
                    }
                  >
                    <Grid container spacing={2}>
                      <Grid item lg={2.4} md={4} sm={6} xs={12}>
                        <Autocomplete
                          sx={autocompleteStyleOverrides}
                          id="location_Autocomplete"
                          label="Select Location *"
                          name="Loc_Autocomplete_Name"
                          open={open}
                          onOpen={() => {
                            setOpen(true);
                          }}
                          onClose={() => {
                            setOpen(false);
                          }}
                          value={values?.Loc_Autocomplete_Name ?? ""}
                          options={
                            locationData?.data?.data?.map((item) => ({
                              value: item.Loc_Auto_ID,
                              label: item.Loc_Name,
                            })) || []
                          }
                          onChange={(event, newValue) => {
                            handleChange(event);
                            handleAutocompleteChange(
                              newValue,
                              "Loc_Autocomplete_Name",
                              "Loc_Auto_ID"
                            );
                          }}
                          onInputChange={(e, value) =>
                            debounceHandlerForSuggestions(value)
                          }
                          isOptionEqualToValue={(option, value) =>
                            option.label === value.label
                          }
                          onBlur={handleBlur}
                          renderInput={(params) => (
                            <MuiTextField
                              {...params}
                              size="small"
                              name="Loc_Autocomplete_Name"
                              label="Select Location *"
                              error={Boolean(
                                touched?.Loc_Autocomplete_Name &&
                                errors?.Loc_Autocomplete_Name
                              )}
                              helperText={
                                touched?.Loc_Autocomplete_Name &&
                                errors?.Loc_Autocomplete_Name
                              }
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <React.Fragment>
                                    {locationData.isLoading ? (
                                      <Spin size="small" />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </React.Fragment>
                                ),
                              }}
                            />
                          )}
                        />
                        {/* <SelectField
                      label="Location Name"
                      name="Loc_Auto_ID"
                      options={locationData?.data?.data?.map((item) => ({
                        value: item.Loc_Auto_ID,
                        label: item.Loc_Name,
                      }))}
                      placeholder="Select Options"
                      isRequired={true}
                    /> */}
                      </Grid>
                      <Grid item lg={2.4} md={4} sm={6} xs={12}>
                        <TextField
                          label="Plotting Code"
                          name="Plot_Code"
                          isRequired={true}
                          disabled={isPlottingUpdated}
                        />
                      </Grid>
                      <Grid item lg={2.4} md={4} sm={4} xs={12}>
                        <TextField
                          label="Plotting Name"
                          name="Plot_Name"
                          isRequired={true}
                        />
                      </Grid>
                      <Grid item lg={2.4} md={4} sm={4} xs={12}>
                        <TextField
                          type="number"
                          label="Total Plotting Area (Hect.)"
                          name="Plot_Area"
                          isRequired={true}
                        />
                      </Grid>
                      <Grid item lg={2.4} md={4} sm={4} xs={12}>
                        <TextField
                          type="number"
                          label="Total Planting Count"
                          name="Total_Planting_Count"
                          isRequired={true}
                        />
                      </Grid>
                      {/* new changes - tag v3.0.1 */}
                      <Grid item lg={2.4} md={4} sm={6} xs={12}>
                        <SelectField
                          label="Land use"
                          name="Land_Use"
                          options={queriesData[0].data?.data?.map((item) => ({
                            value: item.Cat_Value,
                            label: item.Cat_Value,
                          }))}
                          placeholder="Select Options"
                          isRequired={true}
                        />
                      </Grid>
                      <Grid item lg={2.4} md={4} sm={6} xs={12}>
                        <SelectField
                          label="Aspect"
                          name="Aspect"
                          options={queriesData[1].data?.data?.map((item) => ({
                            value: item.Cat_Value,
                            label: item.Cat_Value,
                          }))}
                          placeholder="Select Options"
                          isRequired={true}
                        />
                      </Grid>
                      <Grid item lg={2.4} md={4} sm={6} xs={12}>
                        <SelectField
                          label="Soil Texture"
                          name="Soil_Texture"
                          options={queriesData[2].data?.data?.map((item) => ({
                            value: item.Cat_Value,
                            label: item.Cat_Value,
                          }))}
                          placeholder="Select Options"
                          isRequired={true}
                        />
                      </Grid>
                      <Grid item lg={2.4} md={4} sm={6} xs={12}>
                        <SelectField
                          label="Presence of Weed or Grass"
                          name="Presence_of_Weed_or_Grass"
                          options={[
                            { value: "Yes", label: "Yes" },
                            { value: "No", label: "No" },
                          ].map((item) => ({
                            value: item.value,
                            label: item.label,
                          }))}
                          placeholder="Select Options"
                          isRequired={true}
                        />
                      </Grid>
                      <Grid item lg={6} md={4} sm={12} xs={12}>
                        <GoogleAutocomplete
                          ref={childRef}
                          getPlaceOption={getGoogleAutocomplete}
                          locationValue={address ? address : "Add a location"}
                        />
                      </Grid>
                      <Grid item lg={3} md={4} sm={6} xs={12}>
                        <MuiTextField
                          label="Latitude"
                          name="Latitude"
                          value={latitude || ""}
                          onChange={handleLatitudeChange}
                          required
                          fullWidth
                        />
                      </Grid>
                      <Grid item lg={3} md={4} sm={6} xs={12}>
                        <MuiTextField
                          label="Longitude"
                          name="Longitude"
                          value={longitude || ""}
                          onChange={handleLongitudeChange}
                          required
                          fullWidth
                        />
                      </Grid>
                      <Grid
                        item
                        my={2}
                        lg={12}
                        sx={{ display: "flex", justifyContent: "end" }}
                      >
                        <Stack
                          direction={matchesXs ? "column-reverse" : "row"}
                          spacing={2}
                          useFlexGap
                        >
                          <MDButton
                            sx={{ px: 5 }}
                            variant="contained"
                            color="error"
                            type="reset"
                            size={matchesXs ? "small" : "large"}
                            onClick={() => {
                              if (
                                location.state &&
                                location.pathname.includes("plotting-master/update")
                              ) {
                                navigate(-1);
                              } else {
                                formRef.current?.handleReset();
                              }
                            }}
                          >
                            Cancel
                          </MDButton>
                          {userHasRequiredRole && (
                            <MDButton
                              sx={{ px: 6 }}
                              type="submit"
                              fullWidth
                              variant="contained"
                              color="success"
                              size={matchesXs ? "small" : "large"}
                              onClick={() =>
                                console.log("ON SUBMIT", formRef.current)
                              }
                            >
                              {submitButtonLabel}
                            </MDButton>
                          )}
                        </Stack>
                      </Grid>
                    </Grid>
                  </Spin>
                </form>
              )}
            </Formik>
          </MDBox>
        </Grid>
        <Grid item lg={12} md={12} sm={6} xs={6}>
          <MDBox p={2}>
            {latitude ? (<MyLocationMap
              coordinates={{ lat: +latitude, lng: +longitude }}
              location={address ? address : "Location Name"}
            />
            ) : <Spin />}
          </MDBox>
        </Grid>
      </Grid>
    </GenericFormContainer>
  );
};

export default CreatePlotting;
